/** Max length for password fields (change password, etc.). Validation rule only — not a credential. */
export const PASSWORD_MAX_LENGTH = 20;

/** User-facing message when password exceeds max length. Built from constant to avoid hardcoded values. */
export const getPasswordMaxLengthMessage = (): string => `Password cannot exceed ${PASSWORD_MAX_LENGTH} characters`;

export const emailValidation = (email: unknown) => {
    if (!email || typeof email !== 'string') {
        return 'Please Enter Email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return 'Please Enter a Valid Email';
    } else {
        return '';
    }
};
export const passwordValidation = (password: unknown) => {
    if (!password || typeof password !== 'string') {
        return 'Please Enter Password';
    } else if (password.length < 8) {
        return 'Password must be at least 8 characters long';
    } else if (password.length > 20) {
        return 'Password must be at most 20 characters long';
    } else if (!/[A-Z]/.test(password)) {
        return 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/.test(password)) {
        return 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/.test(password)) {
        return 'Password must contain at least one number';
    } else if (!/[!@#$%^&*(),.?":{}|<>_-]/.test(password)) {
        return 'Password must contain at least one special character';
    } else {
        return '';
    }
};
/** Amount field: max value when principal/balance is 100M or more. */
export const AMOUNT_MAX_VALUE = 99_999_999.99;
/** When principal/outstanding balance is at or above this, amount is capped at AMOUNT_MAX_VALUE. */
export const AMOUNT_PRINCIPAL_THRESHOLD = 100_000_000;

/**
 * Effective max amount the user can enter based on principal/outstanding balance.
 * If balance >= 100M, cap at 99,999,999.99; otherwise cap at balance.
 */
export const getAmountMaxValue = (principalOrBalance: number): number => {
    if (principalOrBalance >= AMOUNT_PRINCIPAL_THRESHOLD) return AMOUNT_MAX_VALUE;
    return principalOrBalance;
};

/**
 * True when `amount` is greater than `max` once both are rounded to whole cents, so float
 * noise in a balance (e.g. 0.2499999 vs a typed 0.25) doesn't block an otherwise valid amount.
 */
export const isAmountOverMax = (amount: number, max: number): boolean =>
    Math.round(amount * 100) > Math.round(max * 100);

/**
 * Max input length for the amount field based on principal/outstanding balance.
 * Matches the string length of the max allowed value (e.g. "99999999.99" -> 11).
 */
export const getAmountMaxLength = (principalOrBalance: number): number => {
    const maxVal = getAmountMaxValue(principalOrBalance);
    const str = maxVal % 1 === 0 ? `${maxVal}.00` : maxVal.toFixed(2);
    return str.length;
};

/** First Name / Last Name (secondary registration): max 16 characters, alphanumeric with spaces. */
export const NAME_MAX_LENGTH = 16;
export const NAME_MAX_SPACES = 3;
export const NAME_ALPHANUMERIC_PATTERN = /^[A-Za-z0-9 ]*$/;

export const firstNameOrLastNameValidation = (value: unknown, fieldLabel: 'First Name' | 'Last Name'): string => {
    if (!value || typeof value !== 'string') {
        return `Please Enter ${fieldLabel}`;
    }
    const trimmed = value.trim();
    if (trimmed === '') {
        return `Please Enter ${fieldLabel}`;
    }
    if (trimmed.length > NAME_MAX_LENGTH) {
        return `${fieldLabel} cannot exceed ${NAME_MAX_LENGTH} characters`;
    }
    if (!NAME_ALPHANUMERIC_PATTERN.test(trimmed)) {
        return `${fieldLabel} can only contain letters, numbers, and spaces`;
    }
    if (/\s{2,}/.test(trimmed)) {
        return `${fieldLabel} cannot contain consecutive spaces`;
    }
    const spaceCount = (trimmed.match(/ /g) || []).length;
    if (spaceCount > NAME_MAX_SPACES) {
        return `${fieldLabel} can contain at most ${NAME_MAX_SPACES} spaces`;
    }
    if (/^\d+$/.test(trimmed.replace(/\s+/g, ''))) {
        return `${fieldLabel} cannot be all numbers`;
    }
    return '';
};

/** Sanitize name input: alphanumeric and spaces only, max 16 characters. */
export const sanitizeNameInput = (value: string): string => {
    const alphanumeric = value.replace(/[^A-Za-z0-9 ]/g, '');
    const withoutConsecutiveSpaces = alphanumeric.replace(/\s{2,}/g, ' ');
    return withoutConsecutiveSpaces.slice(0, NAME_MAX_LENGTH);
};

export const contractIdValidation = (contractId: unknown) => {
    if (!contractId || typeof contractId !== 'string') {
        return 'Please Enter Contract Id';
    } else {
        return '';
    }
};
export const fedTaxNumberValidation = (fedTaxNumber: unknown) => {
    if (!fedTaxNumber || typeof fedTaxNumber !== 'string') {
        return 'Please Enter FED Tax Number';
    } else {
        return '';
    }
};
export const postCodeValidation = (postCode: unknown) => {
    if (!postCode || typeof postCode !== 'string') {
        return 'Please Enter Post Code';
    } else {
        return '';
    }
};
export const dateOfBirthValidation = (dateOfBirth: unknown) => {
    if (!dateOfBirth || typeof dateOfBirth !== 'string') {
        return 'Date of birth is required.';
    } else {
        return '';
    }
};
/**
 * US Social Security Number (SSN): Exactly 9 digits
 * Format: XXX-XX-XXXX (hyphens optional)
 * Validation rules:
 * - First group (area number): not 000 or 666
 * - Second group (group number): not 00
 * - Third group (serial number): not 0000
 */
export const ssnNumberValidation = (ssnNumber: unknown): string => {
    if (!ssnNumber || typeof ssnNumber !== 'string') {
        return 'Please Enter SSN Number';
    }
    const trimmed = ssnNumber.trim();
    if (trimmed === '') {
        return 'Please Enter SSN Number';
    }
    // Remove hyphens for validation
    const digitsOnly = trimmed.replace(/\D/g, '');
    // Check if exactly 9 digits
    if (digitsOnly.length !== 9) {
        return 'SSN must be 9 digits in format (e.g., 123-45-6789)';
    }
    // Extract groups
    const areaNumber = digitsOnly.substring(0, 3);
    const groupNumber = digitsOnly.substring(3, 5);
    const serialNumber = digitsOnly.substring(5, 9);
    // Check first group: not 000 or 666
    if (areaNumber === '000' || areaNumber === '666') {
        return 'Invalid SSN: First group cannot be 000 or 666';
    }
    // Check second group: not 00
    if (groupNumber === '00') {
        return 'Invalid SSN: Second group cannot be 00';
    }
    // Check third group: not 0000
    if (serialNumber === '0000') {
        return 'Invalid SSN: Third group cannot be 0000';
    }
    return '';
};

/**
 * Format SSN input to XXX-XX-XXXX format by removing non-digits and adding hyphens.
 */
export const formatSSNInput = (value: string): string => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    // Limit to 9 digits
    const limitedDigits = digitsOnly.slice(0, 9);
    // Format as XXX-XX-XXXX
    if (limitedDigits.length <= 3) {
        return limitedDigits;
    } else if (limitedDigits.length <= 5) {
        return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
    } else {
        return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 5)}-${limitedDigits.slice(5)}`;
    }
};

/**
 * US Federal Tax ID: 9-digit number, typically formatted as XX-XXXXXXX
 * Format: XX-XXXXXXX (hyphens optional)
 */
export const federalTaxIdValidation = (fedTaxId: unknown): string => {
    if (!fedTaxId || typeof fedTaxId !== 'string') {
        return 'Please Enter Federal Tax Number';
    }
    const trimmed = fedTaxId.trim();
    if (trimmed === '') {
        return 'Please Enter Federal Tax Number';
    }
    // Remove hyphens for validation
    const digitsOnly = trimmed.replace(/\D/g, '');
    // Check if exactly 9 digits
    if (digitsOnly.length !== 9) {
        return 'Fed Tax Number must be 9 digits (e.g., 12-3456789)';
    }
    return '';
};

/**
 * Format Federal Tax ID to XX-XXXXXXX format by removing non-digits and adding hyphen.
 */
export const formatFederalTaxIdInput = (value: string): string => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    // Limit to 9 digits
    const limitedDigits = digitsOnly.slice(0, 9);
    // Format as XX-XXXXXXX
    if (limitedDigits.length <= 2) {
        return limitedDigits;
    } else {
        return `${limitedDigits.slice(0, 2)}-${limitedDigits.slice(2)}`;
    }
};

/**
 * Canada SIN (Social Insurance Number): 9-digit number issued by Service Canada
 * Format: NNN-NNN-NNN (hyphens optional)
 */
export const sinNumberValidation = (sinNumber: unknown): string => {
    if (!sinNumber || typeof sinNumber !== 'string') {
        return 'Please Enter SIN Number';
    }
    const trimmed = sinNumber.trim();
    if (trimmed === '') {
        return 'Please Enter SIN Number';
    }
    // Remove hyphens for validation
    const digitsOnly = trimmed.replace(/\D/g, '');
    // Check if exactly 9 digits
    if (digitsOnly.length !== 9) {
        return 'SIN must be 9 digits';
    }
    return '';
};

/**
 * Format SIN input: only 9 digits, issued by Service Canada. Not formatted with hyphens.
 */
export const formatSINInput = (value: string): string => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    // Limit to 9 digits and return as-is (no formatting)
    return digitsOnly.slice(0, 9);
};

/**
 * Canada Business Number (BN): 9-digit numeric identifier followed by optional alphanumeric characters
 * Format: 9 digits + up to 6 alphanumeric characters (e.g., 123456789RT0001)
 * Total length: 9-15 characters (no hyphens)
 */
export const businessNumberValidation = (businessNumber: unknown): string => {
    if (!businessNumber || typeof businessNumber !== 'string') {
        return 'Please Enter Business Number';
    }
    const trimmed = businessNumber.trim();
    if (trimmed === '') {
        return 'Please Enter Business Number';
    }
    // Check length: minimum 9 characters, maximum 15 characters
    if (trimmed.length < 9 || trimmed.length > 15 || !/^\d{9}/.test(trimmed)) {
        return 'Business number must be 9-15 characters (e.g., 123456789RT0001)';
    }
    return '';
};

/**
 * Format Business Number: accepts 9 digits + up to 6 alphanumeric characters
 * Returns the input as-is (uppercase) with no hyphens (e.g., 123456789RT0001)
 */
export const formatBusinessNumberInput = (value: string): string => {
    // Remove all hyphens and convert to uppercase
    const cleaned = value.replace(/-/g, '').toUpperCase();
    // Limit to 15 characters max and return as-is (no hyphen formatting)
    return cleaned.slice(0, 15);
};
export const phoneNumberValidation = (phoneNumber: unknown) => {
    if (!phoneNumber || typeof phoneNumber !== 'string') {
        return 'Please Enter Phone Number';
    } else {
        // Remove common formatting characters for validation
        const phoneDigits = phoneNumber.replace(/[\s\-()]/g, '');
        // Check if it matches phone regex pattern (allows +country code and 10 digits)
        if (!/^(\+\d{1,3}[- ]?)?\d{10}$/.test(phoneDigits)) {
            return 'Please Enter a Valid Phone Number';
        } else {
            return '';
        }
    }
};

/** Bank name: max 50 chars, alphanumeric and spaces only. */
export const BANK_NAME_MAX_LENGTH = 50;
export const BANK_NAME_SAFE_PATTERN = /^[A-Za-z0-9\s]+$/;

export const bankNameValidation = (bankName: unknown): string => {
    if (!bankName || typeof bankName !== 'string') {
        return 'Bank Name is required';
    }
    const trimmed = bankName.trim();
    if (trimmed === '') {
        return 'Bank Name is required';
    }
    if (trimmed.length > BANK_NAME_MAX_LENGTH) {
        return `Bank name must be ${BANK_NAME_MAX_LENGTH} characters or less`;
    }
    if (!BANK_NAME_SAFE_PATTERN.test(trimmed)) {
        return 'Bank name can only contain letters, numbers, and spaces.';
    }
    if (/^\d+$/.test(trimmed)) {
        return 'Bank name cannot be all numbers';
    }
    return '';
};

/** Sanitize bank name input: allow only alphanumeric and spaces, enforce max length. */
export const sanitizeBankNameInput = (value: string): string => {
    const safe = value.replace(/[^A-Za-z0-9\s]/g, '');
    return safe.slice(0, BANK_NAME_MAX_LENGTH);
};

/** Account number: min 6, max 17 characters, alphanumeric only. Cannot be all same digit or all zeros. */
export const ACCOUNT_NUMBER_MIN_LENGTH = 6;
export const ACCOUNT_NUMBER_MAX_LENGTH = 17;
export const accountNumberValidation = (accountNumber: unknown): string => {
    if (!accountNumber || typeof accountNumber !== 'string') {
        return 'Account Number is required';
    }
    const trimmed = accountNumber.trim();
    if (trimmed === '') {
        return 'Account Number is required';
    }
    if (trimmed.length < ACCOUNT_NUMBER_MIN_LENGTH) {
        return `Account Number must be at least ${ACCOUNT_NUMBER_MIN_LENGTH} digits`;
    }
    if (trimmed.length > ACCOUNT_NUMBER_MAX_LENGTH) {
        return `Account Number should be less than or equal to ${ACCOUNT_NUMBER_MAX_LENGTH} digits`;
    }
    // Check if alphanumeric only
    if (!/^[a-zA-Z0-9]+$/.test(trimmed)) {
        return 'Account Number must contain only alphanumeric characters';
    }
    // Check if all same digit
    if (/^(\d)\1+$/.test(trimmed)) {
        return 'Account Number cannot contain only the same digit (e.g., 0000000000, 1111111111)';
    }
    // Check if all zeros
    if (/^0+$/.test(trimmed)) {
        return 'Account Number cannot contain only zeros';
    }
    return '';
};

/** Re-enter account number: validates same rules as accountNumber, then checks if it matches the original. */
export const reEnterAccountNumberValidation = (
    reEnterAccountNumber: unknown,
    originalAccountNumber: unknown,
    isOriginalValid: boolean,
): string => {
    if (!reEnterAccountNumber || typeof reEnterAccountNumber !== 'string') {
        return 'Re-enter Account Number is required';
    }
    const trimmed = reEnterAccountNumber.trim();
    if (trimmed === '') {
        return 'Re-enter Account Number is required';
    }
    if (trimmed.length < ACCOUNT_NUMBER_MIN_LENGTH) {
        return `Re-enter Account Number must be at least ${ACCOUNT_NUMBER_MIN_LENGTH} digits`;
    }
    if (trimmed.length > ACCOUNT_NUMBER_MAX_LENGTH) {
        return `Re-enter Account Number should be less than or equal to ${ACCOUNT_NUMBER_MAX_LENGTH} digits`;
    }
    // Check if alphanumeric only
    if (!/^[a-zA-Z0-9]+$/.test(trimmed)) {
        return 'Re-enter Account Number must contain only alphanumeric characters';
    }
    // Check if all same digit
    if (/^(\d)\1+$/.test(trimmed)) {
        return 'Re-enter Account Number cannot contain only the same digit (e.g., 0000000000, 1111111111)';
    }
    // Check if all zeros
    if (/^0+$/.test(trimmed)) {
        return 'Re-enter Account Number cannot contain only zeros';
    }
    // Check if original account number is valid before comparing
    if (!isOriginalValid) {
        return 'Please fix the Account Number field first';
    }
    // Check if matches original
    if (
        originalAccountNumber &&
        typeof originalAccountNumber === 'string' &&
        trimmed !== originalAccountNumber.trim()
    ) {
        return 'Account Numbers do not match';
    }
    return '';
};

export const emailOrPhoneValidation = (value: unknown) => {
    if (!value || typeof value !== 'string') {
        return 'Please Enter Email or Phone Number';
    } else {
        const trimmedValue = value.trim();
        // Check if it's an email
        if (trimmedValue.includes('@')) {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) {
                return 'Please Enter a Valid Email';
            }
        } else {
            // Check if it's a phone number
            const phoneDigits = trimmedValue.replace(/[\s\-()]/g, '');
            if (!/^(\+\d{1,3}[- ]?)?\d{10}$/.test(phoneDigits)) {
                return 'Please Enter a Valid Phone Number';
            }
        }
        return '';
    }
};
